/**
 * RTWO Indexer Engine — Asset Indexing Service
 */

import { INDEXER_SERVICE_IDS } from '../domain-models';
import type { IndexedAssetId, RTWOIndexedAsset } from '../domain-models';
import type { AssetIndexResult, IAssetIndexingService } from '../service-interfaces';
import type { IndexerRepositories } from '../repositories/indexer-repositories';
import type { LedgerAdapterRegistry } from '../registry/adapter-registries';
import { IndexerInternalEventTypes } from '../events/indexer-event-bus';
import { BaseIndexerService, type IndexerServiceContext } from './base-indexer-service';

export class AssetIndexingService extends BaseIndexerService implements IAssetIndexingService {
  readonly serviceId = INDEXER_SERVICE_IDS.ASSET_INDEXING;
  readonly serviceName = 'Asset Indexing Service';
  readonly description = 'Indexes assets via Ledger adapter. No fake assets or supplies indexed.';
  readonly integrations = ['RPC Platform', 'Ledger Engine', 'Wallet Engine'];
  readonly requiresRPCSource = true;
  readonly requiresIndexStorage = false;

  constructor(
    context: IndexerServiceContext,
    private readonly repositories: IndexerRepositories,
    private readonly ledgerRegistry: LedgerAdapterRegistry
  ) {
    super(context);
  }

  async indexAsset(assetId: string): Promise<AssetIndexResult> {
    if (!this.ledgerRegistry.isConfigured()) {
      return {
        success: false,
        indexedAssetId: null,
        error:
          'Ledger Engine Waiting for Real Data — Cannot index assets until Ledger Engine is connected.',
        isPlaceholder: true,
        source: 'Indexer Engine — Ledger Engine Waiting for Real Data',
      };
    }

    const adapterResult = await this.ledgerRegistry.getActiveAdapter().indexAsset(assetId);

    if (adapterResult?.asset) {
      this.repositories.assets.save(adapterResult.asset);
      this.context.eventBus.emit(IndexerInternalEventTypes.ASSET_INDEXED, {
        indexedAssetId: adapterResult.asset.indexedAssetId,
        assetId,
      });
    }

    return {
      success: false,
      indexedAssetId: null,
      error:
        'Ledger Engine Waiting for Real Data — Cannot index assets until Ledger Engine is connected.',
      isPlaceholder: true,
      source: 'Indexer Engine — Ledger Engine Waiting for Real Data',
    };
  }

  getAllIndexedAssets(): RTWOIndexedAsset[] {
    if (!this.ledgerRegistry.isConfigured()) {
      return [];
    }

    return this.repositories.assets.findAll();
  }

  getIndexedAsset(indexedAssetId: IndexedAssetId): RTWOIndexedAsset | null {
    if (!this.ledgerRegistry.isConfigured()) {
      return null;
    }

    return this.repositories.assets.findById(indexedAssetId);
  }

  getIndexedAssetCount(): 0 {
    return 0;
  }
}
